import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../../components/ui/dialog'
import { Button } from '../../../../components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import { useGetServiceTypes, useCreateServiceRecord } from '../../../../api/routes/services'
import { toast } from 'react-toastify'

const LogServiceModal = ({
    open,
    onOpenChange,
    deviceId,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
    deviceId: string
}) => {
    const { data: serviceTypes } = useGetServiceTypes(deviceId)
    const createRecord = useCreateServiceRecord()

    const [serviceTypeId, setServiceTypeId] = useState('')
    const [date, setDate] = useState(new Date().toISOString().split('T')[0])
    const [cost, setCost] = useState('')
    const [meterReading, setMeterReading] = useState('')
    const [technician, setTechnician] = useState('')
    const [notes, setNotes] = useState('')

    const resetForm = () => {
        setServiceTypeId('')
        setDate(new Date().toISOString().split('T')[0])
        setCost('')
        setMeterReading('')
        setTechnician('')
        setNotes('')
    }

    const handleSubmit = () => {
        if (!serviceTypeId) {
            toast.error('Please select a service type')
            return
        }

        createRecord.mutate(
            {
                deviceId,
                data: {
                    serviceTypeId,
                    servicedAt: date,
                    cost: cost ? parseFloat(cost) : undefined,
                    meterValue: meterReading ? parseFloat(meterReading) : undefined,
                    performedBy: technician || undefined,
                    notes: notes || undefined,
                },
            },
            {
                onSuccess: () => {
                    toast.success('Service record logged')
                    resetForm()
                    onOpenChange(false)
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message ||
                            error.message ||
                            'Failed to log service record'
                    )
                },
            }
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Log Service</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="serviceType">Service Type</Label>
                        <Select value={serviceTypeId} onValueChange={setServiceTypeId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a service type" />
                            </SelectTrigger>
                            <SelectContent>
                                {(serviceTypes || []).map((type: any) => (
                                    <SelectItem key={type.id} value={type.id}>
                                        {type.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="date">Date</Label>
                        <Input
                            id="date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="cost">Cost</Label>
                            <Input
                                id="cost"
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={cost}
                                onChange={(e) => setCost(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="meterReading">Meter Reading</Label>
                            <Input
                                id="meterReading"
                                type="number"
                                placeholder="Optional"
                                value={meterReading}
                                onChange={(e) => setMeterReading(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="technician">Technician</Label>
                        <Input
                            id="technician"
                            placeholder="Name of technician"
                            value={technician}
                            onChange={(e) => setTechnician(e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="notes">Notes</Label>
                        <textarea
                            id="notes"
                            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="Additional notes..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={createRecord.isPending}>
                        {createRecord.isPending ? 'Saving...' : 'Log Service'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default LogServiceModal
